<template>
  <div>
    <!-- Hero -->
    <section class="home-hero">
      <img class="home-hero-image" src="/images/concept-hero.webp" :alt="t('home.heroAlt')" />
      <div class="home-hero-shade"></div>
      <div class="home-hero-copy">
        <p class="eyebrow">AUTO CARE · PROTECT / COLOR / TINT</p>
        <h1>
          <span class="hero-line">{{ t('home.heroTitle1') }}</span>
          <span class="hero-line">{{ t('home.heroTitle2') }}</span>
        </h1>
        <p>{{ t('home.heroSub') }}</p>
        <div class="hero-cta">
          <router-link to="/contact" class="light-button">{{ t('home.heroContact') }}</router-link>
          <router-link to="/products" class="ghost-button">{{ t('home.heroExplore') }}</router-link>
        </div>
      </div>
      <span class="concept-label">CONCEPT · PPF / WRAP / TINT</span>
    </section>

    <!-- 五大产品族 -->
    <section class="section family-section">
      <div class="section-title-row">
        <div>
          <p class="eyebrow">PRODUCT FAMILIES</p>
          <h2>{{ t('home.familiesTitle1') }}<br />{{ t('home.familiesTitle2') }}</h2>
        </div>
        <router-link to="/products" class="text-link">{{ t('common.allProducts') }}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
        </router-link>
      </div>
      <div class="family-images">
        <router-link
          v-for="(cat, idx) in categories"
          :key="cat.id"
          :to="'/products/' + cat.slug"
          class="family-image-card"
          :style="idx === 4 ? 'grid-column: 1 / -1' : ''"
        >
          <picture v-if="mobileImageOf(cat)">
            <source media="(max-width: 900px)" :srcset="mobileImageOf(cat)" />
            <img :src="cat.image" :alt="tf(cat, 'name')" />
          </picture>
          <img v-else :src="cat.image" :alt="tf(cat, 'name')" />
          <div>
            <p class="eyebrow">{{ tfAlt(cat, 'name') }}</p>
            <h3>{{ tf(cat, 'name') }}</h3>
            <p>{{ tf(cat, 'summary') }}</p>
          </div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
        </router-link>
      </div>
    </section>

    <!-- 精选产品 -->
    <section class="section featured-products">
      <div class="section-title-row">
        <div>
          <p class="eyebrow">FEATURED PRODUCTS</p>
          <h2>{{ t('home.featuredTitle') }}</h2>
        </div>
        <router-link to="/products" class="text-link">{{ t('common.viewAll') }}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
        </router-link>
      </div>
      <div class="model-grid">
        <router-link
          v-for="p in featured"
          :key="p.id"
          :to="'/products/' + p.categorySlug + '/' + p.slug"
          class="model-card"
        >
          <img :src="p.image" :alt="tf(p, 'name')" />
          <div>
            <p class="eyebrow">{{ tfAlt(p, 'name') }}</p>
            <h3>{{ tf(p, 'name') }}</h3>
            <p>{{ tf(p, 'descriptor') }}</p>
            <p class="model-category">{{ categoryOf(p) }}</p>
            <span class="text-link">{{ t('common.viewDetails') }}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
            </span>
          </div>
        </router-link>
      </div>
    </section>

    <!-- 实车案例 -->
    <section class="section home-gallery">
      <div class="section-title-row">
        <div>
          <p class="eyebrow">INSTALLATION GALLERY</p>
          <h2>{{ t('home.galleryTitle') }}</h2>
        </div>
        <router-link to="/gallery" class="text-link">{{ t('home.galleryAll') }}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
        </router-link>
      </div>
      <div class="visual-card-grid">
        <router-link
          v-for="item in installations.slice(0, 3)"
          :key="item.id"
          :to="'/gallery/' + item.id"
          class="visual-card"
        >
          <img :src="item.image" :alt="tf(item, 'title')" />
          <div>
            <h3>{{ tf(item, 'title') }}</h3>
            <p class="visual-code">{{ colorName(item) }}</p>
            <p class="visual-vehicle">{{ item.vehicleMake }} {{ item.vehicleModel }} {{ item.vehicleYear }}</p>
          </div>
        </router-link>
      </div>
    </section>

    <!-- 品牌理念 -->
    <section class="about-home">
      <div class="about-mark" aria-hidden="true">SN</div>
      <div>
        <p class="eyebrow">ABOUT</p>
        <h2>{{ t('home.aboutTitle') }}</h2>
        <p>{{ brand.brandBody }}</p>
        <router-link to="/about" class="text-link">{{ t('home.aboutLink') }}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
        </router-link>
      </div>
    </section>

    <!-- 洞察与新闻 -->
    <section class="section" style="padding-top: 90px; padding-bottom: 100px;">
      <div class="section-title-row">
        <div>
          <p class="eyebrow">INSIGHTS &amp; NEWS</p>
          <h2>{{ t('home.insightsTitle') }}</h2>
        </div>
        <router-link to="/insights" class="text-link">{{ t('common.more') }}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
        </router-link>
      </div>
      <div class="article-rows">
        <router-link v-for="a in articles.slice(0, 2)" :key="a.id" :to="'/insights/' + (a.slug || a.id)" class="article-row">
          <div class="article-row-meta">
            <span>{{ articleTypeLabel(a.type) }}</span>
            <time :datetime="a.publishDate || a.date">{{ formatDate(a.publishDate || a.date) }}</time>
            <span>{{ tf(a, 'readTime') }}</span>
          </div>
          <div>
            <h2>{{ tf(a, 'title') }}</h2>
            <p>{{ tf(a, 'excerpt') }}</p>
            <span class="article-read">{{ t('common.readArticle') }}
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
            </span>
          </div>
        </router-link>
      </div>
    </section>

    <!-- 服务 -->
    <section class="section" style="padding-bottom: 80px;">
      <div class="section-title-row">
        <div>
          <p class="eyebrow">SERVICE</p>
          <h2>{{ t('home.serviceTitle') }}</h2>
        </div>
      </div>
      <div class="service-hub-links">
        <router-link to="/warranty/query" class="service-card">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>
          <div>
            <h2>{{ t('home.serviceWarrantyTitle') }}</h2>
            <p>{{ t('home.serviceWarrantyDesc') }}</p>
          </div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
        </router-link>
        <router-link to="/warranty/authentication" class="service-card">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>
          <div>
            <h2>{{ t('home.serviceAuthTitle') }}</h2>
            <p>{{ t('home.serviceAuthDesc') }}</p>
          </div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
        </router-link>
      </div>
      <div class="support-note">
        <span style="color:#666c71;font-size:.88rem;">{{ t('home.serviceHelp') }}</span>
        <a :href="'mailto:' + brand.supportEmail">{{ brand.supportEmail }}</a>
        <a :href="'tel:' + brand.telephone">{{ brand.telephone }}</a>
      </div>
    </section>

    <!-- 联系区块 -->
    <section class="contact-home">
      <div>
        <p class="eyebrow">CONTACT</p>
        <h2>{{ t('home.contactTitle1') }}<br />{{ t('home.contactTitle2') }}</h2>
      </div>
      <div class="contact-home-links">
        <router-link to="/contact">
          <span>01</span>
          <div>
            <h3>{{ t('home.contactBook') }}</h3>
            <p>{{ t('home.contactBookSub') }}</p>
          </div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
        </router-link>
        <router-link to="/contact">
          <span>02</span>
          <div>
            <h3>{{ t('home.contactSales') }}</h3>
            <p>{{ t('home.contactSalesSub') }}</p>
          </div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
        </router-link>
        <router-link :to="{ path: '/contact', query: { topic: 'support' } }">
          <span>03</span>
          <div>
            <h3>{{ t('home.contactSupport') }}</h3>
            <p>{{ brand.supportEmail }}</p>
          </div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17 17 7M7 7h10v10"/></svg>
        </router-link>
      </div>
    </section>
  </div>
</template>

<script>
import { getOverview, getInstallations, getColors, getArticles } from '../api/content'
import { langMixin } from '../utils/site-lang'
import {
  categories as fbCategories,
  products as fbProducts,
  featuredProducts as fbFeatured,
  installations as fbInstallations,
  colors as fbColors,
  articles as fbArticles,
  brand
} from '../data/site-data'

export default {
  name: 'HomeView',
  mixins: [langMixin],
  data () {
    return {
      // 初始用静态数据兜底，接口返回后覆盖
      categories: fbCategories,
      installations: fbInstallations,
      articles: fbArticles,
      colors: fbColors,
      featuredProducts: fbFeatured,
      products: fbProducts,
      brand
    }
  },
  created () {
    this.loadAll()
  },
  computed: {
    featured () {
      return this.featuredProducts
        .map(id => this.products.find(p => (p.slug || p.id) === id))
        .filter(Boolean)
        .map(p => ({ ...p, categorySlug: p.categorySlug || this.categorySlugOf(p.categoryId) }))
    }
  },
  methods: {
    async loadAll () {
      try {
        const overview = await getOverview()
        if (overview && overview.categories) {
          this.categories = overview.categories
          this.products = (overview.products || []).map(p => ({ ...p, categorySlug: this.categorySlugOf(p.categoryId) }))
          this.featuredProducts = (overview.featuredProducts || []).map(p => p.slug)
        }
      } catch (e) { /* 静态兜底 */ }
      try {
        const installations = await getInstallations()
        if (Array.isArray(installations)) this.installations = installations
      } catch (e) { /* 静态兜底 */ }
      try {
        const colors = await getColors()
        if (Array.isArray(colors)) this.colors = colors
      } catch (e) { /* 静态兜底 */ }
      try {
        const res = await getArticles({ page: 1, size: 10 })
        if (res && Array.isArray(res.list)) this.articles = res.list
      } catch (e) { /* 静态兜底 */ }
    },
    categorySlugOf (categoryId) {
      return (this.categories.find(c => c.id === categoryId) || {}).slug || ''
    },
    // 首页产品族移动端竖版图：与桌面图同目录同名加 -mobile 后缀（约定派生）；
    // 非本站 /images/home-family-* 图片（如后台上传）不派生，移动端沿用原图
    mobileImageOf (cat) {
      const img = cat.image || ''
      if (!img.startsWith('/images/home-family-') || img.includes('-mobile.')) return null
      return img.replace(/(\.webp|\.png|\.jpg|\.jpeg)$/i, '-mobile$1')
    },
    categoryOf (product) {
      const cat = this.categories.find(c => (c.slug || c.id) === product.categorySlug)
      return cat ? this.tf(cat, 'name') : ''
    },
    colorOf (slug) {
      return this.colors.find(c => (c.slug || c.id) === slug) || { name: '', code: '' }
    },
    colorName (item) {
      const c = this.colorOf(item.colorSlug || item.colorId)
      const name = c.name ? this.tf(c, 'name') : ''
      return [name, c.code].filter(Boolean).join(' · ')
    },
    formatDate (d) {
      if (!d) return ''
      const dt = new Date(d)
      if (this.lang === 'en') {
        return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      }
      return `${dt.getFullYear()}年${dt.getMonth() + 1}月${dt.getDate()}日`
    }
  }
}
</script>
